import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  Router,
  type CanActivateFn,
  type UrlTree,
} from '@angular/router';
import { filter, map, take } from 'rxjs';
import { ActiveUserService } from '../services/active-user.service';
import { AuthService } from '../services/auth.service';
import { getSafeReturnUrl, needsDateOfBirthGate } from '../services/auth-navigation';

function dateOfBirthGateDecision(
  activeUserService: ActiveUserService,
  authService: AuthService,
  router: Router,
): true | UrlTree {
  if (authService.isStaffImpersonation()) {
    return true;
  }

  const user = activeUserService.user();
  if (user && needsDateOfBirthGate(user)) {
    return router.createUrlTree(['/onboarding']);
  }

  return true;
}

export const dateOfBirthGateGuard: CanActivateFn = () => {
  const activeUserService = inject(ActiveUserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (activeUserService.userLoaded()) {
    return dateOfBirthGateDecision(activeUserService, authService, router);
  }

  if (activeUserService.userLoadFailed()) {
    return true;
  }

  const profileReady = computed(() => ({
    loaded: activeUserService.userLoaded(),
    failed: activeUserService.userLoadFailed(),
  }));

  return toObservable(profileReady).pipe(
    filter((state) => state.loaded || state.failed),
    take(1),
    map((state) =>
      state.loaded ? dateOfBirthGateDecision(activeUserService, authService, router) : true,
    ),
  );
};

function leaveDateOfBirthGateIfComplete(
  activeUserService: ActiveUserService,
  authService: AuthService,
  router: Router,
  route: ActivatedRouteSnapshot,
): true | UrlTree {
  if (authService.isStaffImpersonation()) {
    return router.parseUrl(getSafeReturnUrl(route.queryParamMap.get('returnUrl')));
  }

  const user = activeUserService.user();
  if (user && !needsDateOfBirthGate(user)) {
    return router.parseUrl(getSafeReturnUrl(route.queryParamMap.get('returnUrl')));
  }

  return true;
}

/** Keeps `/onboarding` from sticking after date of birth is already on the profile. */
export const dateOfBirthGatePageGuard: CanActivateFn = (route) => {
  const activeUserService = inject(ActiveUserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (activeUserService.user() == null) {
    if (activeUserService.userLoadFailed()) {
      return true;
    }
  } else {
    return leaveDateOfBirthGateIfComplete(activeUserService, authService, router, route);
  }

  const profileReady = computed(() => ({
    present: activeUserService.user() != null,
    failed: activeUserService.userLoadFailed(),
  }));

  return toObservable(profileReady).pipe(
    filter((state) => state.present || state.failed),
    take(1),
    map((state) =>
      state.present
        ? leaveDateOfBirthGateIfComplete(activeUserService, authService, router, route)
        : true,
    ),
  );
};
